import React, { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { OnFailureData } from 'Interfaces/Api';
import { LoginFormProps } from 'Interfaces/LoginRegisterFormProps';
import { FormFieldTracking, FormLinkTracking } from 'Interfaces/overlayTracking';
import SubmitButton from 'SharedComponents/SubmitButton';
import SwitchLink from 'SharedComponents/SwitchLink';
import PasswordInput from 'SharedComponents/PasswordInput';
import EmailInput from 'SharedComponents/EmailInput';
import { MagicLinkButton } from 'SharedComponents/styled/forms/MagicLinkButton';
import { Field } from 'SharedComponents/styled/forms/Field';
import useTranslate from 'Hooks/useTranslate';
import useLoginFormTracking from 'Hooks/useLoginFormTracking';
import useLocationChange from 'Hooks/useLocationChange';
import { FormWrapper } from 'SharedComponents/styled/forms/FormWrapper';
import { PasswordRecoveryLink } from 'SharedComponents/styled/forms/RecoveryPasswordLink';
import { ClubRegisterResponse } from 'SharedHelpers/LoginRegisterFormAPI';
import { useErrorHandler } from 'Hooks/useErrorHandler';
import HiddenFields from 'SharedComponents/HiddenFields';
import useApi from 'Hooks/useApi';

const LoginForm: React.FunctionComponent<LoginFormProps> = ({ ...formProps }) => {
    const props = {
        isMagicLinkEnabled: false,
        ...formProps,
    };

    const { setIsLoading, redirectUrl, forgetPasswordRedirectUrl, handleClose } = props;

    const { tPlain } = useTranslate();
    const { login } = useApi();
    const { locationChange } = useLocationChange();
    const { trackFieldFilled, trackFieldError, trackFormSubmit, trackLinkClick } = useLoginFormTracking();
    const { errorHandler } = useErrorHandler();

    const loginFromApi = useForm({
        mode: 'onSubmit',
        reValidateMode: 'onSubmit',
        criteriaMode: 'all',
    });
    const { register, handleSubmit, setError, clearErrors, errors, getValues, watch } = loginFromApi;

    const signupFieldsWatcher = watch(['email', 'password']);

    useEffect(() => {
        if (signupFieldsWatcher.email?.length > 0) {
            trackFieldFilled(FormFieldTracking.EmailFormField);
        }
        if (signupFieldsWatcher.password?.length > 0) {
            trackFieldFilled(FormFieldTracking.PasswordFormField);
        }
    }, [signupFieldsWatcher, trackFieldFilled]);

    useEffect(() => {
        if (errors.email) {
            trackFieldError(FormFieldTracking.EmailFormField);
        }
        if (errors.password) {
            trackFieldError(FormFieldTracking.PasswordFormField);
        }
    }, [errors.email, errors.password, trackFieldError]);

    const handleSwitch = () => {
        props.handleShowLoginToggle();
        // TODO Club
        // removeQueryStrKey('login');
        // updateQueryStr('signup', 1);
        clearErrors();
    };

    const handleEmailChange = () => {
        props.handleEmailChange(getValues('email'));

        clearErrors('email');
    };

    const onPasswordRecoveryClick = () => {
        trackLinkClick(FormLinkTracking.ForgotPasswordLink);
    };

    const trackEmailError = () => {
        trackFieldError(FormFieldTracking.EmailFormField);
    };

    // TODO CLub
    // const onMagicLinkClick = useCallback(async () => {
    //     setIsLoading(true);
    //     if (!(await trigger('email'))) {
    //         setIsLoading(false);
    //         return;
    //     }
    //     const formValues = {
    //         ...getValues(),
    //         login: '0',
    //         // eslint-disable-next-line camelcase
    //         magic_link: '1',
    //     };
    //     const response = await axios.post(`${apiBaseUrl}${LOGIN_API.MAGIC_LINK_URL}`, new URLSearchParams(formValues));
    //     const { status, data } = response;
    //     const { status: dataStatus = STATUS.ERROR } = data;
    //
    //     if (status === HTTP_STATUS_OK && dataStatus === STATUS.SUCCESS) {
    //         window.location.href = LOGIN_API.MAGIC_LINK_REDIRECT_URL;
    //         return;
    //     }
    //     setIsLoading(false);
    //     handleBackendErrors(data, setError);
    // }, [getValues, setIsLoading, apiBaseUrl, handleBackendErrors, trigger, setError]);

    const onSubmit = useCallback(
        async formInput => {
            trackFormSubmit();
            setIsLoading(true);

            // TODO Club
            // const { status, data } = await LoginRegisterFormHelper.submit(`${apiBaseUrl}${LOGIN_API.URL}`, formInput);
            // const { status: dataStatus = STATUS.ERROR } = data;
            //
            // if (status === HTTP_STATUS_OK && dataStatus === STATUS.SUCCESS) {
            //     window.location.href = redirectUrl;
            //     return;
            // }
            //
            // handleBackendErrors(data, setError);
            await login(
                formInput,
                false,
                () => {
                    handleClose();
                    if (redirectUrl) {
                        locationChange(redirectUrl);
                    }
                },
                (loginErrors: OnFailureData<ClubRegisterResponse>) => {
                    const callbacks = { setError, setIsLoading };
                    const submitInfo = { isLastStep: false, isMultiStep: false, redirectUrl, showLogin: true };
                    errorHandler(submitInfo, loginErrors, callbacks);
                }
            );
        },
        [login, trackFormSubmit, setIsLoading, handleClose, redirectUrl, locationChange, setError, errorHandler]
    );

    return (
        <FormWrapper name={props.formName} onSubmit={handleSubmit(onSubmit)} className="qa-login-form">
            <HiddenFields usingRef={() => register()} redirectUrl={redirectUrl} isLogin isReferral={false} />
            <Field>
                <EmailInput
                    backendEmailValidator={props.backendEmailValidator}
                    formApi={loginFromApi}
                    id={props.formIdentifiers?.email}
                    defaultValue={props.inputEmail}
                    readOnly={props.isLoading || !!props.defaultEmail}
                    onChange={handleEmailChange}
                    errorCallback={trackEmailError}
                    autoFocus
                />
            </Field>
            <Field>
                <PasswordInput id={props.formIdentifiers?.password} formApi={loginFromApi} readOnly={props.isLoading} />
            </Field>
            <PasswordRecoveryLink
                onClick={onPasswordRecoveryClick}
                className="qa-login-signup-form__password-recovery-link"
                href={`/customer/account/forgotpassword/?redirectUrl=${
                    forgetPasswordRedirectUrl || redirectUrl
                }&noMagic=${+props.isMagicLinkEnabled}&email=${encodeURIComponent(props.inputEmail)}`}
            >
                {tPlain('signupOverlay_Kein Passwort?')}
            </PasswordRecoveryLink>
            <SubmitButton
                name={props.formIdentifiers?.submit}
                isLoading={props.isLoading}
                buttonText={tPlain('signupOverlay_Login button')}
                className="qa-login-signup-form__submit"
                addWiggleEffect={false}
            />
            {props.isMagicLinkEnabled && (
                <MagicLinkButton
                    kind="secondary"
                    type="button"
                    disabled={props.isLoading}
                    // TODO Club
                    // onClick={onMagicLinkClick}
                >
                    {tPlain('signupOverlay_Send a magic link')}
                </MagicLinkButton>
            )}
            {!props.hideSwitch && (
                <SwitchLink
                    className="qa-login-signup-form__switch"
                    onClick={handleSwitch}
                    isLoading={props.isLoading}
                    introText=""
                    ctaText={tPlain('signupOverlay_Switch to registration')}
                />
            )}
        </FormWrapper>
    );
};

export default LoginForm;
